import React, { useState } from 'react';
import { Search, CheckCircle, Clock, Calendar, AlertCircle, Mail, Phone, QrCode, Eye, EyeOff, Lock } from 'lucide-react';
import { registrationsAPI } from '../utils/api';

const MyBooking = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [showQRCodes, setShowQRCodes] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const handleSearch = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    if (!password.trim()) {
      setError('Please enter your booking password');
      return;
    }

    setLoading(true);
    setError('');
    setRegistrations([]);

    try {
      const response = await registrationsAPI.getMyBookings(email.trim(), password.trim());
      const registrationsData = response.data?.data || response.data || [];
      
      setRegistrations(Array.isArray(registrationsData) ? registrationsData : []);
      setError('');
      
      if (registrationsData.length === 0) {
        setError('No bookings found for this email and password combination');
      }
      
      setSearched(true);
    } catch (err) {
      console.error('Search error:', err);
      if (err.response?.status === 401) {
        setError('Invalid email or booking password. Please check your credentials.');
      } else {
        setError('Unable to retrieve bookings. Please try again later.');
      }
      setRegistrations([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };



  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const getStatusBadge = (isCheckedIn) => {
    if (isCheckedIn) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
          <CheckCircle className="w-4 h-4 mr-1" />
          Checked In
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-4 h-4 mr-1" />
          Registered
        </span>
      );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-black mb-2">My Bookings</h1>
          <p className="text-black">Enter your email and booking password to view your event registrations</p>
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-center text-blue-800">
              <Lock className="w-5 h-5 mr-2" />
              <span className="text-sm font-medium">Secure Access Required</span>
            </div>
            <p className="text-blue-700 text-sm mt-2">
              Your booking password was sent to your email when you registered. Check your confirmation email.
            </p>
          </div>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-black mb-2">
              Booking Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your 4-6 digit booking password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              This password was sent to your email after registration
            </p>
          </div>
          
          <button
            onClick={handleSearch}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition duration-200 flex items-center justify-center space-x-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <Search className="w-5 h-5" />
            <span>{loading ? 'Verifying...' : 'Access My Bookings'}</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Registration Details */}
        {registrations.length > 0 && (
          <div className="space-y-4 animate-fadeIn">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-black">Your Bookings ({registrations.length})</h2>
              <div className="text-sm text-black">
                Found {Array.isArray(registrations) ? registrations.filter(r => r.isCheckedIn).length : 0} checked-in, {Array.isArray(registrations) ? registrations.filter(r => !r.isCheckedIn).length : 0} pending
              </div>
            </div>
            {registrations.map((registration) => (
              <div key={registration.registrationId} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-black">
                      {registration.event?.name || registration.eventName || 'Event Details'}
                    </h3>
                    <p className="text-black">{registration.name}</p>
                  </div>
                  {getStatusBadge(registration.isCheckedIn)}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center text-black">
                    <Mail className="w-4 h-4 mr-2" />
                    <span className="text-sm">{registration.email}</span>
                  </div>
                  <div className="flex items-center text-black">
                    <Phone className="w-4 h-4 mr-2" />
                    <span className="text-sm">{registration.phone}</span>
                  </div>
                  <div className="flex items-center text-black">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="text-sm">
                      {registration.event?.date ? formatDate(registration.event.date) : 'Date TBD'}
                    </span>
                  </div>
                  <div className="flex items-center text-black">
                    <QrCode className="w-4 h-4 mr-2" />
                    <span className="text-sm font-mono">{registration.registrationId}</span>
                  </div>
                </div>

                {/* Event Details */}
                {registration.event && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-black mb-2">Event Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                      {registration.event.time && (
                        <div>Time: {registration.event.time}</div>
                      )}
                      {registration.event.venue && (
                        <div>Venue: {registration.event.venue}</div>
                      )}
                      {registration.ticketTier && (
                        <div>Ticket: {registration.ticketTier.charAt(0).toUpperCase() + registration.ticketTier.slice(1)}</div>
                      )}
                      {registration.numberOfTickets && (
                        <div>Tickets: {registration.numberOfTickets}</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center text-sm text-black mb-3">
                    <span>Registered: {formatDate(registration.createdAt)}</span>
                    <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800">
                      Confirmed
                    </span>
                  </div>
                  
                  {registration.isCheckedIn && registration.checkedInAt && (
                    <div className="text-sm text-green-600 mb-3">
                      ✓ Checked in: {new Date(registration.checkedInAt).toLocaleString()}
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <button
                      onClick={() => setShowQRCodes(prev => ({
                        ...prev,
                        [registration.registrationId]: !prev[registration.registrationId]
                      }))}
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      {showQRCodes[registration.registrationId] ? (
                        <><EyeOff className="w-4 h-4" /> Hide QR Code</>
                      ) : (
                        <><Eye className="w-4 h-4" /> Show QR Code</>
                      )}
                    </button>
                    
                    {registration.whatsappSent && (
                      <span className="text-xs text-green-600">📱 Sent via WhatsApp</span>
                    )}
                  </div>
                  
                  {showQRCodes[registration.registrationId] && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center">
                      <div className="text-xs text-gray-500 mb-2">Your QR Code:</div>
                      {registration.qrCode ? (
                        <div className="bg-white p-4 rounded-lg inline-block">
                          <img 
                            src={registration.qrCode} 
                            alt="Registration QR Code" 
                            className="w-48 h-48 mx-auto"
                          />
                        </div>
                      ) : (
                        <div className="text-red-500 text-sm">QR Code not available</div>
                      )}
                      <div className="text-xs text-gray-500 mt-2">
                        Show this QR code at the event for check-in
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No Results Message */}
        {searched && registrations.length === 0 && !error && !loading && (
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-black mb-2">No Bookings Found</h3>
            <p className="text-black mb-4">We couldn't find any bookings with the provided email and password combination.</p>
            <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4">
              <p className="font-medium mb-2">Tips:</p>
              <ul className="text-left space-y-1">
                <li>• Check your email for the booking password</li>
                <li>• Ensure you're using the correct email address</li>
                <li>• The password is 4-6 digits sent after registration</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default MyBooking;